/**
 * Bulk Configure — apply origin / Multi-CDN / security / cache settings across a
 * set of selected domains at once. The domain set is handed over from the
 * Domains list (router state, with a sessionStorage fallback so a refresh keeps
 * it). Each tab groups bulk sections; every section applies independently via
 * `POST /waf/bulk/<op>`.
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, X, LayoutGrid } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { EmptyState } from '@vnetwork/ui/data';
import {
  Card,
  CardBody,
  IconButton,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@vnetwork/ui/primitives';
import { GeneralSettingsTab } from './GeneralSettingsTab';
import { MultiCdnTab } from './MultiCdnTab';
import { SecurityTab } from './SecurityTab';
import { CacheControlTab } from './CacheControlTab';
import styles from './WafBulkConfigure.module.css';

const ssKey = (serviceId: string) => `waf:${serviceId}:bulk-domains`;

export function WafBulkConfigure({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const location = useLocation();
  const back = () => navigate(`/services/waf/${serviceId}/domains`);

  // Seed from router state, falling back to sessionStorage (survives a refresh).
  const initial = useMemo<string[]>(() => {
    const fromState = (location.state as { domains?: string[] } | null)?.domains;
    if (fromState?.length) return fromState;
    try {
      const raw = sessionStorage.getItem(ssKey(serviceId));
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  }, [location.state, serviceId]);

  const [domains, setDomains] = useState<string[]>(initial);
  useEffect(() => {
    try { sessionStorage.setItem(ssKey(serviceId), JSON.stringify(domains)); } catch { /* ignore */ }
  }, [serviceId, domains]);

  const remove = (d: string) => setDomains((prev) => prev.filter((x) => x !== d));

  if (domains.length === 0) {
    return (
      <Container size="xl">
        <PageHeader title="Bulk Configure" />
        <EmptyState
          icon={<LayoutGrid size={20} />}
          title="No domains selected"
          description="Select one or more domains from the Domains list to bulk-configure them."
          actions={
            <button className={styles.backLink} onClick={back}>
              <ArrowLeft size={15} /> Back to domains
            </button>
          }
        />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <PageHeader title="Bulk Configure" />

      <div className={styles.backBar}>
        <button className={styles.backLink} onClick={back}>
          <ArrowLeft size={15} /> Back to domains
        </button>
        <span className={styles.selCount}>
          <strong>{domains.length}</strong> selected domain{domains.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className={styles.cols}>
        <div className={styles.rail}>
          {domains.map((d) => (
            <div key={d} className={styles.railItem}>
              <span className={styles.railName} title={d}>{d}</span>
              <IconButton variant="ghost" size="sm" aria-label={`Remove ${d}`} onClick={() => remove(d)}>
                <X size={14} />
              </IconButton>
            </div>
          ))}
        </div>

        <Card>
          <CardBody>
            <Tabs defaultValue="general">
              <TabsList>
                <TabsTrigger value="general">General settings</TabsTrigger>
                <TabsTrigger value="multicdn">Multi-CDN</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
                <TabsTrigger value="cache">Cache control</TabsTrigger>
              </TabsList>
              <TabsContent value="general">
                <GeneralSettingsTab serviceId={serviceId} domains={domains} />
              </TabsContent>
              <TabsContent value="multicdn">
                <MultiCdnTab serviceId={serviceId} domains={domains} />
              </TabsContent>
              <TabsContent value="security">
                <SecurityTab serviceId={serviceId} domains={domains} />
              </TabsContent>
              <TabsContent value="cache">
                <CacheControlTab serviceId={serviceId} domains={domains} />
              </TabsContent>
            </Tabs>
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}

export default WafBulkConfigure;
